import { useEffect, useRef, useState } from 'react';

// Single source of truth for whether the app is actually in front of the user.
//
// Two signals are needed. `visibilitychange` covers browser tab switches and is
// reliable on iOS, but inside the Capacitor shell on Android the WebView often
// stays "visible" while the app sits in the background, so timers keep firing at
// full rate. Capacitor's `appStateChange` is the one that reports that correctly.

const listeners = new Set();

let foreground = typeof document === 'undefined' ? true : !document.hidden;
let nativeActive = true;
let pageActive = true;
let disposed = false;
let nativeHandle;
let nativeVersion = 0;
const reconcile = () => notify(pageActive && nativeActive && (typeof document === 'undefined' || !document.hidden));

const notify = (next) => {
  const value = !!next;
  if (value === foreground) return;
  foreground = value;
  listeners.forEach((fn) => {
    try {
      fn(value);
    } catch (error) {
      console.error('[AppState] foreground listener failed:', error);
    }
  });
};

const bindCapacitorAppState = () => {
  const appPlugin = window.Capacitor?.Plugins?.App;
  if (!appPlugin?.addListener) return false;
  if (nativeHandle) return true;
  nativeHandle = Promise.resolve(appPlugin.addListener('appStateChange', ({ isActive }) => {
    if (disposed) return;
    nativeVersion += 1;
    nativeActive = !!isActive;
    reconcile();
  })).catch(() => null);
  const version = nativeVersion;
  appPlugin.getState?.().then(state => {
    if (!disposed && nativeVersion === version) { nativeActive = !!state.isActive; reconcile(); }
  }).catch(() => {});
  return true;
};

const visibility = () => reconcile();
const hide = () => { pageActive = false; reconcile(); };
const show = () => { pageActive = true; reconcile(); };

export function disposeAppState() {
  if (disposed) return;
  disposed = true;
  document.removeEventListener('visibilitychange', visibility);
  window.removeEventListener('pagehide', hide);
  window.removeEventListener('pageshow', show);
  window.removeEventListener('load', bindCapacitorAppState);
  nativeHandle?.then(handle => handle?.remove());
  listeners.clear();
}

if (typeof document !== 'undefined') {
  document.addEventListener('visibilitychange', visibility);
  // BFCache suspends this runtime, it does not open a new session.
  window.addEventListener('pagehide', hide);
  window.addEventListener('pageshow', show);

  if (window.Capacitor?.isNativePlatform?.() && !bindCapacitorAppState()) {
    // The native bridge is normally injected before the bundle runs, but retry
    // once in case a plugin registered late.
    window.addEventListener('load', bindCapacitorAppState, { once: true });
  }
}

export const isForeground = () => foreground;

export const subscribeForeground = (listener) => {
  listeners.add(listener);
  return () => listeners.delete(listener);
};

export function useIsForeground() {
  const [value, setValue] = useState(isForeground);
  useEffect(() => subscribeForeground(setValue), []);
  return value;
}

/**
 * setInterval that only ticks while the app is in front of the user, so background
 * time costs nothing. Pass a null delay to disable. By default the callback also
 * fires immediately on resume, so the UI never shows a value that went stale while
 * the app was away.
 */
export function useForegroundInterval(callback, delayMs, runOnResume = true) {
  const savedCallback = useRef(callback);

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    if (delayMs == null) return undefined;

    let timer = null;
    const start = () => {
      if (timer === null) timer = setInterval(() => savedCallback.current(), delayMs);
    };
    const stop = () => {
      if (timer !== null) {
        clearInterval(timer);
        timer = null;
      }
    };

    if (isForeground()) start();

    const unsubscribe = subscribeForeground((active) => {
      if (!active) {
        stop();
        return;
      }
      if (runOnResume) savedCallback.current();
      start();
    });

    return () => {
      stop();
      unsubscribe();
    };
  }, [delayMs, runOnResume]);
}
